"use client";

import { PrismicRichText } from "@prismicio/react";
import { textToSlug } from "@/helpers";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

const MOBILE_BREAKPOINT = 1024;
const MOBILE_MENU_BOTTOM_MARGIN = 20;
const MOBILE_PANEL_TOP_GAP = 32; // mt-8
const MOBILE_PANEL_BOTTOM_PADDING = 8; // pb-2

const getSafeAreaBottom = () => {
  if (typeof window === "undefined") return 0;

  const probe = document.createElement("div");
  probe.style.cssText =
    "position: fixed; visibility: hidden; pointer-events: none; padding-bottom: env(safe-area-inset-bottom, 0px);";
  document.body.appendChild(probe);
  const inset = parseFloat(getComputedStyle(probe).paddingBottom) || 0;
  document.body.removeChild(probe);
  return inset;
};

const getScrollSpyLine = () => window.innerHeight / 2;

const isMobileViewport = () =>
  typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT;

const ChevronIcon = ({ isOpen }) => (
  <span className="pr-3 transition-all duration-300">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="18"
      viewBox="0 0 12 18"
      fill="none"
      className={`transition-all duration-300 ${isOpen ? "rotate-180" : ""}`}
    >
      <path
        d="M10.8272 11.4463C11.1201 11.7392 11.1201 12.2149 10.8272 12.5078L6.05377 17.2803C5.76087 17.5732 5.28611 17.5732 4.99322 17.2803L0.219782 12.5078C-0.0731111 12.2149 -0.0731111 11.7392 0.219782 11.4463C0.512676 11.1534 0.988412 11.1534 1.28131 11.4463L4.77349 14.9395L4.77349 0.75C4.77349 0.335786 5.10928 -2.59541e-07 5.52349 -2.41435e-07C5.93771 -2.23329e-07 6.27349 0.335786 6.27349 0.75L6.27349 14.9395L9.76568 11.4463C10.0586 11.1534 10.5343 11.1534 10.8272 11.4463Z"
        fill="#053242"
      />
    </svg>
  </span>
);

const ProgressBar = ({ contentHeight, menuScrollTop, progressHeight }) => (
  <div className="relative w-1 shrink-0 self-stretch overflow-hidden" aria-hidden>
    {contentHeight > 0 && (
      <div
        className="absolute left-0 top-0 w-1 bg-vert-10"
        style={{
          height: contentHeight,
          transform: `translateY(-${menuScrollTop}px)`,
        }}
      >
        <div
          className="absolute left-0 top-0 w-1 origin-top bg-vert transition-[height] duration-300 ease-out"
          style={{ height: progressHeight }}
        />
      </div>
    )}
  </div>
);

const MenuPanel = ({
  scrollRef,
  listRef,
  headings,
  headingComponents,
  onScroll,
  contentHeight,
  menuScrollTop,
  progressHeight,
  panelMaxHeight,
  scrollMaxHeight,
  fillAvailableHeight = false,
  rowClassName = "",
  listClassName = "",
  scrollClassName = "",
}) => {
  const isScrollable = Boolean(scrollMaxHeight || fillAvailableHeight);

  return (
  <div
    className={`flex flex-row ${isScrollable ? "min-h-0" : ""} ${rowClassName}`}
    style={panelMaxHeight ? { maxHeight: panelMaxHeight } : undefined}
  >
    <ProgressBar
      contentHeight={contentHeight}
      menuScrollTop={menuScrollTop}
      progressHeight={progressHeight}
    />
    <div
      ref={scrollRef}
      onScroll={onScroll}
      style={scrollMaxHeight ? { maxHeight: scrollMaxHeight } : undefined}
      className={`sumup-menu-scroll ${isScrollable ? "min-h-0 flex-1 overflow-y-auto" : ""} ${scrollMaxHeight ? "overscroll-contain [-webkit-overflow-scrolling:touch]" : ""} ${scrollClassName}`}
    >
      <ul ref={listRef} className={`list-goals-menu flex flex-col gap-3 ${listClassName}`}>
        <PrismicRichText field={headings} components={headingComponents} />
      </ul>
    </div>
  </div>
  );
};

const SideMenu = ({ content, isMobileViewer, mobileAnchorRef }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [activePointer, setActivePointer] = useState("");
  const [contentHeight, setContentHeight] = useState(0);
  const [menuScrollTop, setMenuScrollTop] = useState(0);
  const [progressHeight, setProgressHeight] = useState(0);
  const [mobileMenuAvailableHeight, setMobileMenuAvailableHeight] = useState(0);

  const menuScrollRef = useRef(null);
  const menuListRef = useRef(null);
  const desktopMenuRef = useRef(null);
  const mobileMenuScrollRef = useRef(null);
  const mobileMenuListRef = useRef(null);
  const mobileHeaderRef = useRef(null);

  const headings = useMemo(
    () =>
      content
        .map((el) => el.primary?.rich_text)
        .filter(Array.isArray)
        .flat()
        .filter((el) => el?.type === "heading2"),
    [content],
  );

  const getMenuScrollRef = () =>
    isMobileViewport() ? mobileMenuScrollRef.current : menuScrollRef.current;

  const getMenuListRef = () =>
    isMobileViewport() && mobileMenuListRef.current
      ? mobileMenuListRef.current
      : menuListRef.current;

  const updateMobileMenuAvailableHeight = useCallback(() => {
    const header = mobileHeaderRef.current;
    if (!header || !isMobileViewport()) return;

    const headerRect = header.getBoundingClientRect();
    const bottomInset = getSafeAreaBottom();
    setMobileMenuAvailableHeight(
      Math.max(
        0,
        window.innerHeight -
          headerRect.bottom -
          MOBILE_MENU_BOTTOM_MARGIN -
          bottomInset,
      ),
    );
  }, []);

  const goToSection = useCallback((e) => {
    const text = e.currentTarget.dataset.pointer;
    setIsOpen(false);

    setTimeout(() => {
      const section = document.getElementById(text);
      if (section) {
        window.scrollTo({
          behavior: "smooth",
          top:
            section.getBoundingClientRect().top -
            document.body.getBoundingClientRect().top -
            130,
        });
      }
    }, 100);
  }, []);

  const headingComponents = useMemo(
    () => ({
      heading2: ({ children, text }) => {
        const slug = textToSlug(text);
        const isActive = activePointer === slug;

        return (
          <li
            onClick={goToSection}
            data-pointer={slug}
            className={`cursor-pointer article-sumup-title p-blog text-bleu ${isActive ? "text-sumup-active" : ""}`}
          >
            {children}
          </li>
        );
      },
    }),
    [activePointer, goToSection],
  );

  const handleMenuScroll = useCallback((event) => {
    setMenuScrollTop(event.currentTarget.scrollTop);
  }, []);

  const updateActiveSection = useCallback(() => {
    const triggers = Array.from(document.querySelectorAll(".sumup-trigger"));
    if (!triggers.length) return;

    const scrollSpyLine = getScrollSpyLine();
    let active = triggers[0].id;

    for (const trigger of triggers) {
      if (trigger.getBoundingClientRect().top <= scrollSpyLine) {
        active = trigger.id;
      }
    }

    setActivePointer(active);
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;

    if (!isMobileViewport()) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }

    const anchor = mobileAnchorRef?.current;
    if (!anchor) return;

    const scrollY = window.scrollY;
    const { body, documentElement } = document;
    const rect = anchor.getBoundingClientRect();

    const spacer = document.createElement("div");
    spacer.dataset.sommaireSpacer = "true";
    spacer.style.height = `${rect.height}px`;
    spacer.style.width = "100%";
    anchor.parentNode?.insertBefore(spacer, anchor);

    anchor.style.position = "fixed";
    anchor.style.top = `${rect.top}px`;
    anchor.style.left = `${rect.left}px`;
    anchor.style.right = `${window.innerWidth - rect.right}px`;
    anchor.style.width = "auto";
    anchor.style.zIndex = "50";

    const syncSpacerHeight = () => {
      spacer.style.height = `${anchor.getBoundingClientRect().height}px`;
    };

    const resizeObserver = new ResizeObserver(syncSpacerHeight);
    resizeObserver.observe(anchor);
    syncSpacerHeight();

    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    body.style.touchAction = "none";
    documentElement.style.overflow = "hidden";

    if (mobileMenuScrollRef.current) {
      mobileMenuScrollRef.current.style.touchAction = "pan-y";
    }

    const isInsideMenuScroller = (event) => {
      const scrollEl = mobileMenuScrollRef.current;
      if (!scrollEl) return false;

      const path = event.composedPath?.() ?? [];
      if (path.includes(scrollEl)) return true;

      return event.target instanceof Node && scrollEl.contains(event.target);
    };

    const preventPageScroll = (event) => {
      if (isInsideMenuScroller(event)) return;
      event.preventDefault();
    };

    document.addEventListener("touchmove", preventPageScroll, { passive: false });
    document.addEventListener("wheel", preventPageScroll, { passive: false });

    return () => {
      document.removeEventListener("touchmove", preventPageScroll);
      document.removeEventListener("wheel", preventPageScroll);
      resizeObserver.disconnect();
      spacer.remove();

      anchor.style.position = "";
      anchor.style.top = "";
      anchor.style.left = "";
      anchor.style.right = "";
      anchor.style.width = "";
      anchor.style.zIndex = "";

      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.width = "";
      body.style.overflow = "";
      body.style.touchAction = "";
      documentElement.style.overflow = "";

      if (mobileMenuScrollRef.current) {
        mobileMenuScrollRef.current.style.touchAction = "";
      }

      window.scrollTo(0, scrollY);
    };
  }, [isOpen, mobileAnchorRef]);

  useEffect(() => {
    const triggers = document.querySelectorAll(".sumup-trigger");
    if (!triggers.length) return;

    if (isMobileViewer) {
      const scrollSpyLine = getScrollSpyLine();
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setActivePointer(entry.target.id);
          });
        },
        {
          root: null,
          rootMargin: `-${Math.round(scrollSpyLine - 1)}px 0px -${Math.round(scrollSpyLine - 1)}px 0px`,
          threshold: 0,
        },
      );

      triggers.forEach((element) => observer.observe(element));
      return () => observer.disconnect();
    }

    window.addEventListener("scroll", updateActiveSection, { passive: true });
    window.addEventListener("resize", updateActiveSection, { passive: true });
    updateActiveSection();

    return () => {
      window.removeEventListener("scroll", updateActiveSection);
      window.removeEventListener("resize", updateActiveSection);
    };
  }, [isMobileViewer, headings.length, updateActiveSection]);

  useLayoutEffect(() => {
    const list = getMenuListRef();
    if (!list) return;

    const updateHeight = () => setContentHeight(list.scrollHeight);
    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(list);
    return () => observer.disconnect();
  }, [headings.length, isOpen]);

  useLayoutEffect(() => {
    const container = getMenuScrollRef();
    if (!container || !activePointer) return;

    const activeItem = container.querySelector(`[data-pointer="${activePointer}"]`);
    if (!activeItem) return;

    const itemTop = activeItem.offsetTop;
    const itemHeight = activeItem.offsetHeight;
    setProgressHeight(itemTop === 0 ? itemHeight : itemTop);
  }, [activePointer, contentHeight, isOpen]);

  useEffect(() => {
    const container = getMenuScrollRef();
    if (!container || !activePointer) return;

    const activeItem = container.querySelector(`[data-pointer="${activePointer}"]`);
    if (!activeItem) return;

    const containerHeight = container.clientHeight;
    const itemTop = activeItem.offsetTop;
    const itemHeight = activeItem.offsetHeight;
    const maxScroll = Math.max(container.scrollHeight - containerHeight, 0);
    const centeredScroll = Math.max(
      0,
      Math.min(maxScroll, itemTop - containerHeight / 2 + itemHeight / 2),
    );

    container.scrollTo({ top: centeredScroll, behavior: "smooth" });
  }, [activePointer, contentHeight, isOpen]);

  useLayoutEffect(() => {
    if (!isOpen || !isMobileViewport()) return;

    updateMobileMenuAvailableHeight();
    window.addEventListener("resize", updateMobileMenuAvailableHeight);
    window.visualViewport?.addEventListener("resize", updateMobileMenuAvailableHeight);

    return () => {
      window.removeEventListener("resize", updateMobileMenuAvailableHeight);
      window.visualViewport?.removeEventListener("resize", updateMobileMenuAvailableHeight);
    };
  }, [isOpen, updateMobileMenuAvailableHeight, contentHeight]);

  useEffect(() => {
    const wrapper = desktopMenuRef.current;
    if (!wrapper) return;

    const handleWheel = (event) => {
      if (isMobileViewport()) return;
      event.preventDefault();
      window.scrollBy({ top: event.deltaY, left: 0, behavior: "auto" });
    };

    wrapper.addEventListener("wheel", handleWheel, { passive: false, capture: true });
    return () => wrapper.removeEventListener("wheel", handleWheel, { capture: true });
  }, [headings.length]);

  useEffect(() => {
    const handleScroll = () => {
      if (!isMobileViewport()) {
        setIsAtBottom(false);
        return;
      }

      const scrollPosition = window.innerHeight + window.scrollY;
      const bottomPosition = document.body.offsetHeight;
      setIsAtBottom(bottomPosition - scrollPosition < window.innerHeight * 1.2);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const mobilePanelContentHeight =
    contentHeight + MOBILE_PANEL_TOP_GAP + MOBILE_PANEL_BOTTOM_PADDING;
  const mobilePanelNeedsScroll =
    isOpen &&
    mobileMenuAvailableHeight > 0 &&
    mobilePanelContentHeight > mobileMenuAvailableHeight;
  const mobilePanelMaxHeight = mobilePanelNeedsScroll
    ? mobileMenuAvailableHeight
    : undefined;
  const mobileScrollMaxHeight = mobilePanelNeedsScroll
    ? Math.max(0, mobileMenuAvailableHeight - MOBILE_PANEL_TOP_GAP)
    : undefined;

  return (
    <div className="w-full pointer-events-none text-bleu lg:pointer-events-auto">
      {/* Mobile */}
      <div
        className={`sumup-wrapper pointer-events-auto relative z-50 w-full transition-opacity duration-300 lg:hidden ${isAtBottom && !isOpen ? "opacity-0" : "opacity-100"}`}
      >
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.button
              type="button"
              aria-label="Fermer le sommaire"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="fixed inset-0 z-40 bg-bleu/60"
              onClick={() => setIsOpen(false)}
            />
          )}
        </AnimatePresence>

        <div className="relative z-50 rounded-[16px] border border-vert-10 bg-white p-5 transition-all duration-300">
          <button
            ref={mobileHeaderRef}
            type="button"
            aria-expanded={isOpen}
            className="flex w-full items-center justify-between p-lg-blog text-left text-bleu"
            onClick={() => setIsOpen(!isOpen)}
          >
            Sommaire
            <ChevronIcon isOpen={isOpen} />
          </button>

          <AnimatePresence initial={false}>
            {isOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="z-50 overflow-hidden bg-white"
                style={mobilePanelMaxHeight ? { maxHeight: mobilePanelMaxHeight } : undefined}
              >
                <MenuPanel
                  scrollRef={mobileMenuScrollRef}
                  listRef={mobileMenuListRef}
                  headings={headings}
                  headingComponents={headingComponents}
                  onScroll={handleMenuScroll}
                  contentHeight={contentHeight}
                  menuScrollTop={menuScrollTop}
                  progressHeight={progressHeight}
                  panelMaxHeight={mobilePanelMaxHeight}
                  scrollMaxHeight={mobileScrollMaxHeight}
                  rowClassName="mt-8"
                  listClassName="pb-2"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Desktop */}
      <div
        ref={desktopMenuRef}
        className="sumup-wrapper hidden w-full max-h-[500px] grid-cols-1 grid-rows-[auto_minmax(0,1fr)] gap-6 overflow-hidden rounded-2xl border border-vert-10 px-7 py-5 lg:grid"
      >
        <div className="p-lg-blog text-center">Sommaire</div>
        <MenuPanel
          scrollRef={menuScrollRef}
          listRef={menuListRef}
          headings={headings}
          headingComponents={headingComponents}
          onScroll={handleMenuScroll}
          contentHeight={contentHeight}
          menuScrollTop={menuScrollTop}
          progressHeight={progressHeight}
          fillAvailableHeight
          rowClassName="min-h-0 h-full overflow-hidden"
        />
      </div>
    </div>
  );
};

export default SideMenu;
